import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './Destination.module.scss';

import DestinationOption from '../../components/DestinationOption/DestinationOption';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import {
  ADDRESS_STEP,
  BANK_STATUS,
  BANK_STEP,
  DESTINATION_STEP,
  destinationOptions,
  TOLL_FREE_NUMBER,
  DISTRIBUTION_METHOD_STEP,
  LANDING_STEP,
  REINVEST_STEP,
  REINVEST_TO_NEW,
  destinationOptionsReinvestToNew,
} from '../../settings/settings';
import { getSelectedDestination } from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import {
  getExistingBanks,
  getExistingBanksLoaded,
} from '../../redux/modules/existingBanks/existingBanks.selectors';
import { isNil } from 'lodash';
import {
  calculateAndFormatUnixDates,
  futureDate,
} from '../../utils/date-utils';
import { EXIT_MODAL } from '../../redux/modules/modals/modals.module';
import ModalLink from '../../components/Shared/Modal/ModalLink/ModalLink';
import Phone from '../../components/Phone/Phone';
import HelpBox from '../../components/HelpBox/HelpBox';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import {
  getAddressChangeFlag,
  getEffectiveDate,
  getReturnMailFlagForAddresses,
  checkForAccountTypes,
  checkForEligibleClosedAccounts,
  getPuchaseBlockedForFund,
  getNonRetirementFundsCount,
  doAddressesExist,
} from '../../redux/modules/UserFunds/userFunds.selectors';
import config from '../../config/config';
import { getSalesforceRep } from '../../redux/modules/applicationConfig/applicationConfig.selectors';
import {
  getApplicationDataHasFailed,
  getApplicationDataIsLoading,
} from '../../redux/modules/applicationData/applicationData.selectors';
import Loader from '../../components/Loader/Loader';
import ErrorPage from '../landing/ErrorPage';

const Destination = (props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const isSalesforceRep = useSelector((state) => getSalesforceRep(state));
  const banksLoaded = useSelector(getExistingBanksLoaded);
  const isLoading = useSelector(getApplicationDataIsLoading);
  const hasError = useSelector(getApplicationDataHasFailed);
  const userBanks = useSelector(getExistingBanks);
  const effectiveOldDate = useSelector(getEffectiveDate);
  const isRestrictedByAddressChange = useSelector(getAddressChangeFlag);
  const isReturnMailActiveFlag = useSelector(getReturnMailFlagForAddresses);
  const addressExists = useSelector(doAddressesExist);
  const reinvestEligibleAccountsCheck = useSelector(checkForAccountTypes);
  const nonRetirementFundCount = useSelector(getNonRetirementFundsCount);
  const reinvestClosedEligibleAccountsCheck = useSelector(
    checkForEligibleClosedAccounts
  );
  const isPurchaseBlocked = useSelector(getPuchaseBlockedForFund);
  const currentSelection = useSelector(getSelectedDestination);
  const [selectedDestination, setSelectedDestination] =
    useState(currentSelection);
  const isSearchLoading = useSelector(
    (state) => state.userFunds.isExchangeFundsLoading
  );
  if (isLoading || isSearchLoading) {
    return (
      <div className={styles.loading}>
        <Loader isVisible={true} />
      </div>
    );
  }

  if (hasError) {
    return <ErrorPage />;
  }

  const disableExchange =
    (!reinvestEligibleAccountsCheck ||
      isPurchaseBlocked ||
      nonRetirementFundCount === 0) &&
    selectedDestination === 'EXCHANGE';
  function getIsDisabled(option) {
    if (option.value === BANK_STEP) {
      if (userBanks.length === 0) {
        return {
          isDisabled: true,
          hideDisplay: false,
          text: (
            <div className={styles['dest-error-container']}>
              <div className={styles['error-title']}>{option.text}</div>
              <div className={styles['error-description']}>No Bank on File</div>
              {!isSalesforceRep && (
                <ModalLink
                  link={<div className={styles['error-link']}>Add a bank</div>}
                  linkStyle="link"
                  modalProps={{
                    url: 'https://www.troweprice.com/usis/transactions/addbank',
                    description:
                      'Your information will not be saved and you will not be able to set up a direct deposit RMD to a new bank until after a 5-day holding period.',
                  }}
                  modalName={EXIT_MODAL}
                />
              )}
            </div>
          ),
        };
      } else if (userBanks.length === 1) {
        switch (userBanks[0].bankStatus) {
          case BANK_STATUS.VERIFY_MICRO_DEPOSITS:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div className={styles['dest-error-container']}>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    Bank Ready for Verification
                  </div>
                  <ModalLink
                    link={
                      <div className={styles['error-link']}>
                        Verify Micro-deposits
                      </div>
                    }
                    linkStyle="link"
                    modalProps={{
                      url: 'https://www.troweprice.com/usis/my-accounts/profile/linked-bank-accounts',
                    }}
                    modalName={EXIT_MODAL}
                  />
                </div>
              ),
            };
          case BANK_STATUS.PREPARING_MICRO_DEPOSITS:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    <span className={styles.important}>Pending</span> –
                    Preparing Micro-deposits (typically takes 2-3 days)
                  </div>
                </div>
              ),
            };
          case BANK_STATUS.PROCESSING:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    <span className={styles.important}>Pending</span> –
                    Available on{' '}
                    <span>
                      {!isNil(userBanks[0].availbeForRedemtionDate) &&
                        calculateAndFormatUnixDates(
                          userBanks[0].availbeForRedemtionDate
                        )}
                    </span>
                  </div>
                </div>
              ),
            };
          case BANK_STATUS.LAPSED:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div className={styles['dest-error-container']}>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    Bank Expired
                  </div>
                  <div className={styles['error-link-description']}>
                    Call <Phone phoneNumber={TOLL_FREE_NUMBER} /> to update
                    expired bank or{' '}
                  </div>

                  {!isSalesforceRep ? (
                    <span>
                      <ModalLink
                        link={<div styleName="error-link">add a new bank</div>}
                        linkStyle="link"
                        modalProps={{
                          url: 'https://www.troweprice.com/usis/transactions/addbank',
                        }}
                        modalName={EXIT_MODAL}
                      />
                    </span>
                  ) : (
                    <span>add a new bank</span>
                  )}
                </div>
              ),
            };
          case BANK_STATUS.CORRUPT_BANK_DATA:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div className={styles['dest-error-container']}>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    Bank Requires Update
                  </div>
                  <div className={styles['error-link-description']}>
                    Call <Phone phoneNumber={TOLL_FREE_NUMBER} /> to update bank
                    or{' '}
                  </div>
                  <ModalLink
                    link={
                      <div className={styles['error-link']}>add a new bank</div>
                    }
                    linkStyle="link"
                    modalProps={{
                      url: 'https://www.troweprice.com/usis/transactions/addbank',
                    }}
                    modalName={EXIT_MODAL}
                  />
                </div>
              ),
            };
          case BANK_STATUS.NOT_ASSOCIATED:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    Bank Not Linked
                  </div>
                  <div>
                    <div className={styles['error-link-description']}>
                      Call <Phone phoneNumber={TOLL_FREE_NUMBER} /> for
                      assistance.
                    </div>
                  </div>
                </div>
              ),
            };
          case BANK_STATUS.NOT_LINKED:
            return {
              isDisabled: true,
              hideDisplay: false,
              text: (
                <div className={styles['dest-error-container']}>
                  <div className={styles['error-title']}>{option.text}</div>
                  <div className={styles['error-description']}>
                    Bank Not Linked
                  </div>
                  <span className={styles['bank-flex']}>
                    <ModalLink
                      link={
                        <div className={styles['error-link-bank']}>
                          Link your bank
                        </div>
                      }
                      linkStyle="link"
                      modalProps={{
                        url: `${config.url}/usis/my-accounts/profile/linked-bank-accounts`,
                      }}
                      modalName={EXIT_MODAL}
                    />
                    <span className={styles['error-description-bank']}>
                      to your IRAs
                    </span>
                  </span>
                </div>
              ),
            };
          default:
            return {
              isDisabled: false,
              hideDisplay: false,
            };
        }
      } else {
        if (userBanks.every((bank) => bank.bankStatus)) {
          return {
            isDisabled: true,
            hideDisplay: false,
            text: (
              <div className={styles['dest-error-container']}>
                <div className={styles['error-title']}>{option.text}</div>
                <div className={styles['error-description']}>
                  None of your banks are available for this option
                </div>
                <ModalLink
                  link={
                    <div className={styles['error-link']}>
                      Manage Linked Bank Accounts
                    </div>
                  }
                  linkStyle="link"
                  modalProps={{
                    url: 'https://www.troweprice.com/usis/my-accounts/profile/linked-bank-accounts',
                  }}
                  modalName={EXIT_MODAL}
                />
              </div>
            ),
          };
        }
      }
    } else if (option.value === ADDRESS_STEP) {
      if (isRestrictedByAddressChange) {
        return {
          isDisabled: true,
          hideDisplay: false,
          text: (
            <>
              {option.text}
              <br />
              <div className={styles['restricted-address']}>
                <strong>Pending Verification</strong> - Available on{' '}
                {calculateAndFormatUnixDates(futureDate(effectiveOldDate, 15))}
              </div>
            </>
          ),
        };
      }
      if (isReturnMailActiveFlag || !addressExists) {
        return {
          isDisabled: true,
          hideDisplay: false,
          text: (
            <>
              {option.text}
              <br />
              <div className={styles['restricted-address']}>
                Address Marked as Undeliverable
              </div>
              <ModalLink
                link={
                  <div
                    aria-label="change-address-button"
                    className="error-link"
                    role="link"
                  >
                    Change address
                  </div>
                }
                linkStyle="link"
                modalProps={{
                  url: 'https://www.troweprice.com/usis/my-accounts/profile/contact-information',
                  description:
                    'Your information will not be saved and you will not be able to mail in an RMD to your new address until after the 15 day holding period.',
                }}
                modalName={EXIT_MODAL}
              />
            </>
          ),
        };
      }
      return { isDisabled: false };
    } else if (option.value === REINVEST_TO_NEW) {
      return {
        isDisabled: false,
        showSubtext: true,
        hideDisplay: nonRetirementFundCount !== 0,
        text: (
          <>
            {option.text}
            <br />
            <div className={styles['restricted-address']}>
              Open a new Individual registration to reinvest your RMD. You will
              be able to select a fund next.
            </div>
          </>
        ),
      };
    } else if (option.value === REINVEST_STEP) {
      if (isReturnMailActiveFlag) {
        return {
          isDisabled: true,
          hideDisplay: false,
          text: (
            <>
              {option.text}
              <br />
              <div className={styles['restricted-address']}>
                Address Marked as Undeliverable
              </div>
              <ModalLink
                link={
                  <div
                    aria-label="change-address-button"
                    className="error-link"
                    role="link"
                  >
                    Change address
                  </div>
                }
                linkStyle="link"
                modalProps={{
                  url: 'https://www.troweprice.com/usis/my-accounts/profile/contact-information',
                  description:
                    'Your information will not be saved and you will not be able to mail in an RMD to your new address until after the 15 day holding period.',
                }}
                modalName={EXIT_MODAL}
              />
            </>
          ),
        };
      }
      if (nonRetirementFundCount === 0) {
        return {
          isDisabled: true,
          hideDisplay: true,
          text: (
            <>
              {option.text}
              <br />
              <div className={styles['restricted-address']}>
                {!isSalesforceRep ? (
                  <span>
                    <a
                      className={styles['error-link']}
                      href={`${config.url}/personal-investing/tools/fund-research/account-finder`}
                    >
                      Open a non-retirement account
                    </a>{' '}
                    so you can reinvest your RMD or{' '}
                    <a
                      href={`${config.url}/personal-investing/help/contact/index.html`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      contact us
                    </a>{' '}
                    <span className="text-nowrap">for assistance.</span>
                  </span>
                ) : (
                  <span>
                    Open a non-retirement account so you can reinvest your RMD
                    or{' '}
                    <a
                      href="https://www.troweprice.com/personal-investing/help/contact/index.html"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      contact us
                    </a>{' '}
                    <span className="text-nowrap">for assistance.</span>
                  </span>
                )}
              </div>
              <div className={styles['restricted-address']}>
                When opening your account, please allow one business day for
                processing before scheduling{' '}
                <span className="text-nowrap">your RMD.</span>
              </div>
            </>
          ),
        };
      }
      if (!reinvestEligibleAccountsCheck || isPurchaseBlocked) {
        return {
          isDisabled: true,
          hideDisplay: false,
          text: (
            <>
              {option.text}
              <br />
              <div className={styles['restricted-address']}>
                {!reinvestEligibleAccountsCheck
                  ? 'No Eligible Non-Retirement Account'
                  : isPurchaseBlocked
                  ? 'Transactions Currently Restricted'
                  : ''}{' '}
              </div>
              {!reinvestClosedEligibleAccountsCheck && (
                <div
                  className={styles['restricted-address']}
                  style={{ marginTop: '16px' }}
                >
                  Call <Phone phoneNumber={TOLL_FREE_NUMBER} /> for assistance.
                </div>
              )}
            </>
          ),
        };
      }
    }
    return { isDisabled: false, hideDisplay: false };
  }
  const checkDestinationOptions = () => {
    const getSelectedOption = destinationOptions.find(
      (ele) => ele.value === selectedDestination
    );
    return getIsDisabled(getSelectedOption).isDisabled;
  };
  return (
    <div className="rmd-distribution-wrapper">
      <div className="sticky-header">
        <div className="heading">Auto-RMD Setup</div>
        <ProgressButtons
          {...props}
          edit={props.edit}
          currentSelection={currentSelection}
          currentStep={DESTINATION_STEP}
          nextStep={
            selectedDestination === REINVEST_STEP ||
            selectedDestination === REINVEST_TO_NEW
              ? DISTRIBUTION_METHOD_STEP
              : selectedDestination
          }
          previousStep={LANDING_STEP}
          showBackButton={true}
          backDisabled={false}
          disabled={
            !selectedDestination || disableExchange || checkDestinationOptions()
          }
          payload={selectedDestination}
        >
          <ProgressBar />
        </ProgressButtons>
      </div>
      <PageWrapper step={DESTINATION_STEP}>
        <div className="scroll-wrapper">
          <div className="rmd-title">
            Where should we send <span className="orphan-line"> your RMD?</span>
          </div>
          {banksLoaded ? (
            <div className={styles['option-container']}>
              {destinationOptions.map((option, key) => {
                const status = getIsDisabled(option);
                return status.hideDisplay ? (
                  <span key={key}></span>
                ) : (
                  <DestinationOption
                    selectOption={() => setSelectedDestination(option.value)}
                    isSelected={
                      option.value === selectedDestination && !status.isDisabled
                    }
                    selectable={true}
                    optionText={
                      status.isDisabled || status.showSubtext ? (
                        status.text
                      ) : (
                        <>{option.text}</>
                      )
                    }
                    value={option.value}
                    key={key}
                    disabled={status.isDisabled}
                    destinationStep={true}
                  />
                );
              })}
              <HelpBox step={DESTINATION_STEP} />
              <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
            </div>
          ) : (
            <div> Loading... </div>
          )}
        </div>
      </PageWrapper>
    </div>
  );
};

export default Destination;
